import { ArrowsClockwiseIcon, CaretDownIcon, CaretRightIcon, LinkSimple, PlugsConnected } from "@phosphor-icons/react";
import { type ReactNode, useEffect, useState } from "react";
import { ResourceTreeAction, ResourceTreeRow } from "../../ResourceExplorerTree.tsx";
import type { WorkerBinding } from "../../api/worker-bindings/model.ts";
import { useWorkerBindings } from "./react.tsx";

interface WorkerBindingsExplorerProps {
  workspaceId: string | null;
  connectionName: string;
  collapsed: boolean;
  headerLeading?: ReactNode;
  refreshToken?: number;
  onCollapsedChange: (collapsed: boolean) => void;
}

function targetLabel(binding: WorkerBinding): string {
  return `${binding.workerName}#${binding.exportName ?? "default"}`;
}

export function WorkerBindingsExplorer({
  workspaceId,
  connectionName,
  collapsed,
  headerLeading,
  refreshToken,
  onCollapsedChange
}: WorkerBindingsExplorerProps) {
  const [connectionExpanded, setConnectionExpanded] = useState(true);
  const view = useWorkerBindings(workspaceId, refreshToken);
  const snapshot = view.snapshot.value ?? { workerName: "", bindings: [] };
  const loading = view.snapshot.loading, error = view.snapshot.error, refresh = view.refresh;
  useEffect(() => { setConnectionExpanded(true); }, [workspaceId, refreshToken]);

  return (
    <section className="flex h-full min-h-0 w-full flex-col border-t border-kumo-line">
      <div className="flex h-7 shrink-0 items-center border-b border-kumo-line/60 px-1">
        {headerLeading ?? (
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={() => onCollapsedChange(!collapsed)}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent px-1 text-left text-[10px] font-semibold uppercase tracking-wide text-kumo-default"
          >
            {collapsed ? (
              <CaretRightIcon size={10} weight="bold" />
            ) : (
              <CaretDownIcon size={10} weight="bold" />
            )}
            Worker bindings
          </button>
        )}
        <ResourceTreeAction
          label="Refresh Worker bindings"
          onClick={() => void refresh()}
        >
          <ArrowsClockwiseIcon
            size={13}
            className={loading ? "animate-spin" : ""}
          />
        </ResourceTreeAction>
      </div>

      {!collapsed ? (
        <div role="tree" className="min-h-0 flex-1 overflow-auto py-1">
          {workspaceId && snapshot.bindings.length ? (
            <>
              <ResourceTreeRow
                depth={0}
                label={connectionName || snapshot.workerName || "Workerspace"}
                detail={`${snapshot.bindings.length} binding${snapshot.bindings.length === 1 ? "" : "s"}`}
                icon={<PlugsConnected size={14} />}
                expandable
                expanded={connectionExpanded}
                onClick={() => setConnectionExpanded((value) => !value)}
              />
              {connectionExpanded
                ? snapshot.bindings.map((binding) => (
                    <ResourceTreeRow
                      key={binding.bindingName}
                      depth={1}
                      label={binding.bindingName}
                      detail={targetLabel(binding)}
                      icon={<LinkSimple size={14} weight="bold" />}
                      title={`${binding.self ? "Self binding" : "Station service binding"}${binding.props ? `\nctx.props = ${JSON.stringify(binding.props, null, 2)}` : ""}`}
                    />
                  ))
                : null}
            </>
          ) : workspaceId ? (
            <div className="mx-2 mt-2 rounded border border-dashed border-kumo-line p-2 text-[11px] leading-4 text-kumo-subtle">
              Declare bindings.worker({`{ workerName }`}) in
              cloudflare.config.ts, save, then refresh.
            </div>
          ) : (
            <p className="p-2 text-[11px] text-kumo-subtle">
              Open a workerspace to inspect Worker bindings.
            </p>
          )}
          {error ? (
            <p className="px-2 py-1 text-[11px] text-status-error">{error}</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
